import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

// Context
import { CreateOrderProvider, useCreateOrderContext } from './context';

// Components
import ModalCancelQuotation from './components/modalCancelQuotation/modalCancelQuotation';
import ModalReQuote from './components/modalReQuote/modalReQuote';

// Enums & Utils
import { OrderStepsEnum } from '@/main/services/enum/OrderStepsEnum';
import { getTranslateByKey, isShowControlByElementId } from '@/main/services/utils';

// Redux
import { resetReducerAddressByBodyshop } from '@/main/store/addressByBodyshop';
import { resetReducerCart } from '@/main/store/cart';

// Styles
import { Controls } from '@/main/services/constants/constantsControl';
import * as S from './styles';

export function CreateOrderScreen() {
  const dispatch = useDispatch();
  const {
    currentStep,
    steps,
    handleStepClick,
    setCurrentStep,
    setShowSupplierParts,
    setPartGuid,
    setResetSteps,
    setIsCompareSupplier,
    savedData,
    setIsSeeSuppliersAvailable,
    setExternalPartId,
    setIsQuoteCanceled,
    setIsQuoteManual,
    setIsQuoteOpen,
    setIntegration,
    resultQuoteDetail
  } = useCreateOrderContext();
  const refRenderSteps = useRef<boolean>(false);
  const [searchParams] = useSearchParams();
  const step = searchParams.get('step');
  const guidQuote = searchParams.get('guidQuote');

  const findCurrentStep = steps.find(step => step.id === currentStep);
  const StepComponent = findCurrentStep ? findCurrentStep.component : null;

  // Notation: This logic was created for the scenario of clicking on the car in the header for a new record
  useEffect(() => {
    if (
      Number(step) === OrderStepsEnum.VehicleInformation &&
      (guidQuote === undefined || guidQuote === null) &&
      !refRenderSteps.current
    ) {
      setCurrentStep(OrderStepsEnum.VehicleInformation);
      setShowSupplierParts(false);
      setPartGuid('');
      dispatch(resetReducerCart());
      dispatch(resetReducerAddressByBodyshop());
      setResetSteps(true);
      setIsCompareSupplier(false);
      setIsSeeSuppliersAvailable(false);
      setExternalPartId(null);
      refRenderSteps.current = true;
      setIsQuoteCanceled(false);
      setIsQuoteManual(false);
      setIsQuoteOpen(false);
      setIntegration(false);
    }
  }, [step, guidQuote]);

  useEffect(() => {
    if (refRenderSteps.current) {
      refRenderSteps.current = false;
    }
  }, [refRenderSteps.current]);

  useEffect(() => {
    return () => {
      dispatch(resetReducerCart());
      dispatch(resetReducerAddressByBodyshop());
    };
  }, []);

  return (
    <>
      <S.HeaderText>
        {getTranslateByKey('information.order')}
        {' > '}
        {getTranslateByKey('information.createOrder')}
      </S.HeaderText>
      <S.AreaSteps>
        <S.QuoteNumber>
          {
            resultQuoteDetail?.code && resultQuoteDetail?.code > 0 &&
            `${getTranslateByKey('information.quoteNumber')} ${resultQuoteDetail?.code}`
          }
        </S.QuoteNumber>
        <S.ContainerStep>
          <S.StepCenterLine />
          {steps.map(step => {
            const IconComponent = step.icon;
            return (
              <S.StepButton
                type="button"
                key={step.id}
                active={step.id === currentStep}
                onClick={() => handleStepClick(step.id)}>
                <IconComponent />
              </S.StepButton>
            );
          })}
        </S.ContainerStep>
      </S.AreaSteps>

      <S.ContainerStepComponent>
        {!refRenderSteps.current && StepComponent && <StepComponent />}
      </S.ContainerStepComponent>
      <ModalCancelQuotation />
      <ModalReQuote />
    </>
  );
}

export default function CreateOrderScreenLoaded() {
  return (
    <CreateOrderProvider>
      <CreateOrderScreen />
    </CreateOrderProvider>
  );
}
